import {HttpStatusCode} from '@angular/common/http';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {IncidentApiService, ReferenceBookResponse, ReferenceBookType, ResolutionApiService, ResolutionStatus} from '@npck/af-frontend-api';
import {RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {combineLatest, EMPTY, mergeMap} from 'rxjs';
import {mapToViewModel} from '../../../../components/files/file-preview/file-preview.component';
import {isFinalResolutionStatus, mapResolutionStatusToStyle} from '../../../../components/status/status.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {INCIDENT_ID, SUBJECT_ID} from '../../../../domain/query-params.keys';
import {Resolution, ResolutionService} from '../../../../domain/resolution.service';
import {Path} from '../../../../path';
import {SubjectsTab} from '../../../subjects/subjects.component';

@Component({
    selector: 'af-incident-resolution-tab',
    templateUrl: './incident-resolution-tab.component.html',
    styleUrl: './incident-resolution-tab.component.scss',
    standalone: false,
})
export class IncidentResolutionTabComponent implements OnInit {
    @Input() incidentId: string;
    @Output() reloadIncidentListener = new EventEmitter<void>();
    Spinner = Spinner;
    resolution: Resolution | null = null;
    incidentStatusDict: ReferenceBookResponse[] = [];
    spinnerIds: string[] | string = ['resolutions', 'subjects'];
    activeTab: SubjectsTab;

    constructor(
        private incidentApiService: IncidentApiService,
        private resolutionApiService: ResolutionApiService,
        private dialogService: DialogService,
        private resolutionService: ResolutionService,
        private authHolderService: AuthHolderService,
        private routerService: RouterService,
        private dictionaryHolderService: DictionaryHolderService,
        private activatedRoute: ActivatedRoute,
    ) {
    }

    ngOnInit(): void {
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as SubjectsTab || 'tabSubjectEventsAndAttempts';
        combineLatest([
            this.resolutionApiService.getResolution(this.incidentId),
            this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
            this.dictionaryHolderService.getReference(ReferenceBookType.INCIDENT_STATUS),
        ])
            .pipe(
                rxSpinner('resolutions'),
            )
            .subscribe({
                next: ([incident, docTypes, incidentStatusDict]) => {
                    this.incidentStatusDict = incidentStatusDict;
                    this.resolution = incident ? <Resolution>{
                        id: incident.id,
                        fields: this.resolutionService.fieldBuilder(incident),
                        status: incident.status,
                        createdDate: incident.createdDate,
                        statusStyle: mapResolutionStatusToStyle(incident.status),
                        isInnocentStatus: incident.isInnocentStatus,
                        attachments: (incident.attachments ?? []).map(a => mapToViewModel(a, docTypes)),
                        solutionInfo: incident.solutionInfo,
                    } : null;
                },
                error: err => {
                    if (err.status !== HttpStatusCode.NotFound) {
                        throw err;
                    }
                },
            });
    }

    statusLabel(status: ResolutionStatus): string {
        return findValueInDictionary(this.incidentStatusDict, status, 'name_ru');
    }

    canDelete(resolution: Resolution | null): boolean {
        if (!resolution || resolution.isInnocentStatus) {
            return false;
        }
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    navigateToSubjects(subjectId: string): void {
        this.routerService.navigate(Path.INCIDENTS_SUBJECT_DETAILS, {
            pathParams: {
                [INCIDENT_ID]: this.incidentId,
                [SUBJECT_ID]: subjectId,
            },
            queryParamsHandling: 'merge',
        });
    }

    deleteResolution(deletedResolution: Resolution): void {
        this.dialogService.openDialog({
            title: 'i18n_af_incidentResolutionDeletionDialogTitle',
            subtitle: 'i18n_af_incidentResolutionDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {status: this.statusLabel(deletedResolution.status)},
            isButtonOkAccent: true,
        }).pipe(
            mergeMap(dialogResult => !dialogResult && EMPTY || this.resolutionApiService.deleteResolution(deletedResolution.id)),
            rxSpinner(`delete_${deletedResolution.id}`),
        )
            .subscribe(() => {
                const wasFinal = isFinalResolutionStatus(deletedResolution.status);
                this.resolution = null;
                if (wasFinal) {
                    this.reloadIncidentListener.emit();
                }
            });
    }

}


